import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileCheck,
  Search,
  Download,
  BarChart3,
  TrendingUp,
  Award,
  Users,
  Filter,
  Target,
  Clock,
  BookOpen,
  ExternalLink,
  Activity,
  ShieldAlert,
} from 'lucide-react';
import {
  Card,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
  Button,
  Badge,
  StatCard,
  Input,
  SearchInput,
} from '@azyntor-labs/nexus-ui';
import {
  examsApi,
  analyticsApi,
  gradebookApi,
} from '@/api/endpoints';
import { Exam } from '@/types';
import { toast } from '@/utils/toast';

interface GradeEntry {
  attempt_id: string;
  student_id: string;
  student_name: string;
  student_email: string;
  score: number;
  total_marks: number;
  percentage: number;
  grade: string;
  passed: boolean;
  submitted_at: string;
  time_taken?: number;
  feedback?: string;
}

interface GradeStats {
  average_score: number;
  pass_rate: number;
  highest_score: number;
  lowest_score: number;
  total_attempts: number;
  total_students: number;
  graded_count: number;
  median_score: number;
  std_deviation: number;
}

export const ExamResultsDetailPage = () => {
  const navigate = useNavigate();
  const { examId: urlExamId } = useParams<{ examId: string }>();
  const [selectedExamId, setSelectedExamId] = useState(urlExamId || '');
  const [examSearchTerm, setExamSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch overall tenant analytics
  const { data: tenantRes } = useQuery({
    queryKey: ['tenant-analytics-results'],
    queryFn: () => analyticsApi.getTenantAnalytics(),
    staleTime: 60000,
  });

  const tenantStats = tenantRes?.data?.data as {
    total_exams?: number;
    total_attempts?: number;
    total_students?: number;
    active_exams?: number;
    average_pass_rate?: number;
  } | null;

  // Fetch exams
  const { data: examsRes } = useQuery({
    queryKey: ['exams-for-results'],
    queryFn: () => examsApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  const exams: Exam[] = Array.isArray(examsRes?.data?.data?.data)
    ? examsRes.data.data.data
    : Array.isArray(examsRes?.data?.data)
      ? examsRes.data.data
      : [];

  // Sync search term with selected exam name when exams load (for URL-based selection)
  useEffect(() => {
    if (selectedExamId && exams.length > 0 && !examSearchTerm) {
      const exam = exams.find((e) => e.id === selectedExamId);
      if (exam) setExamSearchTerm(exam.title);
    }
  }, [selectedExamId, exams.length]);

  // Fetch grades
  const { data: gradesRes, isLoading: gradesLoading } = useQuery({
    queryKey: ['exam-grades-detail', selectedExamId],
    queryFn: () => gradebookApi.getExamGrades(selectedExamId, { per_page: 100 }),
    enabled: !!selectedExamId,
    staleTime: 15000,
  });

  const grades: GradeEntry[] = Array.isArray(gradesRes?.data?.data)
    ? (gradesRes.data.data as GradeEntry[])
    : [];

  // Fetch grade stats
  const { data: statsRes } = useQuery({
    queryKey: ['exam-grade-stats-detail', selectedExamId],
    queryFn: () => gradebookApi.getGradeStats(selectedExamId),
    enabled: !!selectedExamId,
    staleTime: 15000,
  });

  const stats = (statsRes?.data?.data || null) as GradeStats | null;

  // Filter grades
  const filtered = grades.filter(
    (g) =>
      g.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.student_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Exam search suggestions
  const examSuggestions = exams
    .filter((exam) => exam.title.toLowerCase().includes(examSearchTerm.toLowerCase()))
    .map((exam) => ({ id: exam.id, label: exam.title }));

  const handleExamChange = (examId: string) => {
    setSelectedExamId(examId);
    setSearchTerm('');
    navigate(`/teacher/exam-results/exam/${examId}`, { replace: true });
  };

  // Export handler
  const handleExport = async () => {
    if (!selectedExamId) return;
    try {
      const response = await gradebookApi.exportGrades(selectedExamId);
      const blob = new Blob([response.data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exam-results-${selectedExamId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Results exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const passedCount = filtered.filter((g) => g.passed).length;
  const failedCount = filtered.filter((g) => !g.passed).length;

  const getGradeBadgeVariant = (passed: boolean): 'success' | 'danger' => {
    return passed ? 'success' : 'danger';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Exam Results</h1>
          <p className="text-white/50 mt-1">View detailed results, risk scores, and violation analytics</p>
        </div>
        {selectedExamId && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ShieldAlert className="w-4 h-4" />}
              onClick={() => navigate(`/teacher/exams/${selectedExamId}/suspicious-attempts`)}
              className="!text-amber-300 !border-amber-500/40 hover:!bg-amber-500/10"
            >
              Scan for cheating
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Download className="w-4 h-4" />}
              onClick={handleExport}
            >
              Export CSV
            </Button>
          </div>
        )}
      </div>

      {/* Overall Stats - always visible */}
      {tenantStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard
            title="Total Exams"
            value={tenantStats.total_exams ?? 0}
            icon={<BookOpen className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Attempts"
            value={tenantStats.total_attempts ?? 0}
            icon={<BarChart3 className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Students"
            value={tenantStats.total_students ?? 0}
            icon={<Users className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Active Exams"
            value={tenantStats.active_exams ?? 0}
            icon={<Activity className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Avg Pass Rate"
            value={`${(tenantStats.average_pass_rate ?? 0).toFixed(1)}%`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Exam Stats Row - show when exam is selected */}
      {selectedExamId && stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard
            title="Average Score"
            value={`${stats.average_score?.toFixed(1) || '0'}%`}
            icon={<BarChart3 className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Pass Rate"
            value={`${stats.pass_rate?.toFixed(1) || '0'}%`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Attempts"
            value={stats.total_attempts || grades.length}
            icon={<Users className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Highest Score"
            value={`${stats.highest_score?.toFixed(1) || '0'}%`}
            icon={<Award className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Lowest Score"
            value={`${stats.lowest_score?.toFixed(1) || '0'}%`}
            icon={<Target className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Median"
            value={`${stats.median_score?.toFixed(1) || '0'}%`}
            icon={<BarChart3 className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Exam Selector + Results Table */}
      <Card>
        {/* Exam Selector */}
        <div className="p-4 border-b border-white/[0.06]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-white/50 mb-1.5">Select Exam</label>
              <SearchInput
                placeholder="Search exams..."
                value={examSearchTerm}
                onChange={(value) => setExamSearchTerm(value)}
                suggestions={examSuggestions}
                onSuggestionSelect={(suggestion) => {
                  handleExamChange(String(suggestion.id));
                  setExamSearchTerm(suggestion.label);
                }}
                showClear
                size="md"
              />
            </div>
            <div className="flex items-end">
              <div className="text-sm text-white/40 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                {selectedExamId
                  ? `Viewing results for: ${exams.find(e => e.id === selectedExamId)?.title || 'Selected exam'}`
                  : 'Select an exam to view results'}
              </div>
            </div>
          </div>
        </div>

        {/* Search & Summary Bar */}
        {selectedExamId && (
          <div className="p-4 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  placeholder="Search students..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-72"
                />
              </div>
              <div className="flex items-center gap-4">
                {filtered.length > 0 && (
                  <>
                    <span className="text-xs text-white/40">
                      {filtered.length} student{filtered.length !== 1 ? 's' : ''}
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant="success" size="sm">{passedCount} passed</Badge>
                      <Badge variant="danger" size="sm">{failedCount} failed</Badge>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Results Table */}
        {!selectedExamId ? (
          <div className="py-16 text-center">
            <FileCheck className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">Select an exam to view results</p>
            <p className="text-xs text-white/40 mt-1">Use the search above to find and select an exam</p>
          </div>
        ) : gradesLoading ? (
          <div className="py-16 text-center">
            <div className="spinner mx-auto mb-4"></div>
            <p className="text-white/60">Loading results...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileCheck className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/60">No results found</p>
            <p className="text-xs text-white/40 mt-1">
              {searchTerm ? 'Try a different search term' : 'No graded attempts for this exam yet'}
            </p>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Student</TableHeader>
                <TableHeader>Score</TableHeader>
                <TableHeader>Grade</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Submitted</TableHeader>
                <TableHeader>Time</TableHeader>
                <TableHeader>Details</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((entry) => (
                <TableRow key={entry.attempt_id}>
                  <TableCell>
                    <div>
                      <div className="font-medium text-white">{entry.student_name}</div>
                      <div className="text-sm text-white/50">{entry.student_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <span className="text-white/90 font-medium">{entry.percentage?.toFixed(1)}%</span>
                      <span className="text-white/40 text-sm ml-2">({entry.score}/{entry.total_marks})</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs text-white/70 bg-white/[0.04] px-2 py-1 rounded">
                      {entry.grade || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getGradeBadgeVariant(entry.passed)} size="sm">
                      {entry.passed ? 'Passed' : 'Failed'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-white/50 text-sm">
                    {entry.submitted_at ? new Date(entry.submitted_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-white/50 text-sm">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {entry.time_taken ? `${Math.round(entry.time_taken / 60)}m` : '-'}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<ExternalLink className="w-3.5 h-3.5" />}
                      onClick={() => navigate(`/teacher/exam-results/attempt/${entry.attempt_id}`)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};
